/**
 * Region Management Component
 * Admin interface for managing map regions
 * Note: Regions are currently hidden from users, preparing for future release
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-hot-toast";
import {
  Globe,
  Search,
  Eye,
  MapPin,
  Calendar,
  Users,
  Map,
  ChevronDown,
  ChevronUp,
  Star,
} from "lucide-react";
import {
  RegionWithStats,
  RegionStatus,
  RegionVisibility,
  REGION_STATUS_LABELS,
  REGION_VISIBILITY_LABELS,
  REGION_STATUS_COLORS,
  REGION_VISIBILITY_COLORS,
} from "../../types/regions";
import {
  getRegionsWithStats,
  updateRegionStatus,
  updateRegionVisibility,
  setDefaultRegion,
  initializeRegions,
} from "../../services/regionService";

const Container = styled.div`
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const Title = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color);
`;

const Badge = styled.span`
  background: var(--primary-color);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  padding-left: 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.875rem;
  background: var(--background-color);
  color: var(--text-color);
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const SearchWrapper = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-secondary-color);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.875rem;
  background: var(--background-color);
  color: var(--text-color);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const InitButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const RegionCard = styled.div<{ $expanded: boolean }>`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px var(--shadow-color);
  }
`;

const CardHeader = styled.div`
  padding: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  cursor: pointer;
`;

const RegionIcon = styled.div<{ $color?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) => props.$color || "var(--primary-color)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const RegionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RegionName = styled.h4`
  margin: 0 0 0.25rem 0;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DefaultStar = styled.span`
  color: #f59e0b;
`;

const RegionLocalName = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary-color);
  font-weight: normal;
`;

const StatusBadges = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span<{ $color: string }>`
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${(props) => props.$color}20;
  color: ${(props) => props.$color};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: var(--text-secondary-color);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--text-color);
  }
`;

const CardBody = styled.div<{ $visible: boolean }>`
  display: ${(props) => (props.$visible ? "block" : "none")};
  padding: 0 1rem 1rem;
  border-top: 1px solid var(--border-color);
`;

const Description = styled.p`
  margin: 1rem 0;
  font-size: 0.875rem;
  color: var(--text-secondary-color);
  line-height: 1.5;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--text-secondary-color);

  svg {
    width: 14px;
    height: 14px;
  }

  strong {
    color: var(--text-color);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: "primary" | "success" | "warning" | "danger" }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s;

  background: ${(props) => {
    switch (props.$variant) {
      case "success":
        return "#22c55e";
      case "warning":
        return "#f59e0b";
      case "danger":
        return "#ef4444";
      case "primary":
      default:
        return "var(--primary-color)";
    }
  }};
  color: white;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusSelect = styled.select`
  padding: 0.375rem 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.75rem;
  background: var(--background-color);
  color: var(--text-color);
  cursor: pointer;
`;

const HistoricalPeriods = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

const PeriodTitle = styled.h5`
  margin: 0 0 0.75rem 0;
  font-size: 0.8rem;
  color: var(--text-secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PeriodList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PeriodBadge = styled.span`
  padding: 0.25rem 0.5rem;
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.7rem;
  color: var(--text-secondary-color);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary-color);

  svg {
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h4 {
    margin: 0 0 0.5rem 0;
    color: var(--text-color);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary-color);
`;

const RegionManagement: React.FC = () => {
  const [regions, setRegions] = useState<RegionWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | RegionStatus>("all");
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    loadRegions();
  }, []);

  const loadRegions = async () => {
    setLoading(true);
    try {
      const regionsData = await getRegionsWithStats();
      setRegions(regionsData);
    } catch (error) {
      console.error("Error loading regions:", error);
      toast.error("Failed to load regions");
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeRegions = async () => {
    setInitializing(true);
    try {
      await initializeRegions();
      toast.success("Regions initialized successfully");
      await loadRegions();
    } catch (error) {
      console.error("Error initializing regions:", error);
      toast.error("Failed to initialize regions");
    } finally {
      setInitializing(false);
    }
  };

  const handleStatusChange = async (regionId: string, newStatus: RegionStatus) => {
    try {
      await updateRegionStatus(regionId, newStatus);
      toast.success("Region status updated");
      await loadRegions();
    } catch (error) {
      console.error("Error updating region status:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to update region status: ${errorMessage}`);
    }
  };

  const handleVisibilityChange = async (
    regionId: string,
    newVisibility: RegionVisibility
  ) => {
    try {
      await updateRegionVisibility(regionId, newVisibility);
      toast.success("Region visibility updated");
      await loadRegions();
    } catch (error) {
      console.error("Error updating region visibility:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to update region visibility: ${errorMessage}`);
    }
  };

  const handleSetDefault = async (regionId: string) => {
    try {
      await setDefaultRegion(regionId);
      toast.success("Default region updated");
      await loadRegions();
    } catch (error) {
      console.error("Error setting default region:", error);
      toast.error("Failed to set default region");
    }
  };

  const toggleExpand = (regionId: string) => {
    setExpandedRegions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(regionId)) {
        newSet.delete(regionId);
      } else {
        newSet.add(regionId);
      }
      return newSet;
    });
  };

  // Filter regions
  const filteredRegions = regions.filter((region) => {
    // Status filter
    if (statusFilter !== "all" && region.status !== statusFilter) {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesName = region.name.toLowerCase().includes(search);
      const matchesLocalNames = Object.values(region.localizedNames).some(
        (name) => name?.toLowerCase().includes(search)
      );
      const matchesTags = region.tags?.some((tag) =>
        tag.toLowerCase().includes(search)
      );
      const matchesDescription = region.description
        ?.toLowerCase()
        .includes(search);

      if (!matchesName && !matchesLocalNames && !matchesTags && !matchesDescription) {
        return false;
      }
    }

    return true;
  });

  if (loading) {
    return (
      <Container>
        <LoadingState>Loading regions...</LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <Globe size={20} />
          Region Management
          <Badge>{regions.length} Regions</Badge>
        </Title>
        <Controls>
          <SearchWrapper>
            <Search size={16} />
            <SearchInput
              type="text"
              placeholder="Search regions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrapper>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as "all" | RegionStatus)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="coming_soon">Coming Soon</option>
            <option value="hidden">Hidden</option>
            <option value="deprecated">Deprecated</option>
          </FilterSelect>
          <InitButton onClick={handleInitializeRegions} disabled={initializing}>
            {initializing ? "Initializing..." : "Initialize Regions"}
          </InitButton>
        </Controls>
      </Header>

      {filteredRegions.length === 0 ? (
        <EmptyState>
          <Globe size={48} />
          <h4>No regions found</h4>
          <p>
            {searchTerm || statusFilter !== "all"
              ? "Try adjusting your search or filter criteria."
              : 'Click "Initialize Regions" to add predefined regions.'}
          </p>
        </EmptyState>
      ) : (
        <RegionGrid>
          {filteredRegions.map((region) => (
            <RegionCard key={region.id} $expanded={expandedRegions.has(region.id)}>
              <CardHeader onClick={() => toggleExpand(region.id)}>
                <RegionIcon $color={region.themeColor}>
                  {region.flagEmoji || <Globe size={24} color="white" />}
                </RegionIcon>
                <RegionInfo>
                  <RegionName>
                    {region.name}
                    {region.isDefault && (
                      <DefaultStar title="Default region">
                        <Star size={14} fill="#f59e0b" />
                      </DefaultStar>
                    )}
                    {region.localizedNames.fa && (
                      <RegionLocalName>({region.localizedNames.fa})</RegionLocalName>
                    )}
                  </RegionName>
                  <StatusBadges>
                    <StatusBadge $color={REGION_STATUS_COLORS[region.status]}>
                      {REGION_STATUS_LABELS[region.status]}
                    </StatusBadge>
                    <StatusBadge $color={REGION_VISIBILITY_COLORS[region.visibility]}>
                      {REGION_VISIBILITY_LABELS[region.visibility]}
                    </StatusBadge>
                  </StatusBadges>
                </RegionInfo>
                <ExpandButton>
                  {expandedRegions.has(region.id) ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </ExpandButton>
              </CardHeader>

              <CardBody $visible={expandedRegions.has(region.id)}>
                {region.description && (
                  <Description>{region.description}</Description>
                )}

                <StatsGrid>
                  <StatItem>
                    <Map size={14} />
                    <span>
                      Maps: <strong>{region.mapsCount ?? region.stats?.totalMaps ?? 0}</strong>
                    </span>
                  </StatItem>
                  <StatItem>
                    <Eye size={14} />
                    <span>
                      Public: <strong>{region.publicMapsCount ?? region.stats?.publicMaps ?? 0}</strong>
                    </span>
                  </StatItem>
                  <StatItem>
                    <Users size={14} />
                    <span>
                      Users: <strong>{region.usersCount ?? region.stats?.totalUsers ?? 0}</strong>
                    </span>
                  </StatItem>
                  <StatItem>
                    <MapPin size={14} />
                    <span>
                      Area: <strong>{region.area?.toLocaleString() || "N/A"}</strong> km²
                    </span>
                  </StatItem>
                </StatsGrid>

                <ActionButtons>
                  <StatusSelect
                    value={region.status}
                    onChange={(e) =>
                      handleStatusChange(region.id, e.target.value as RegionStatus)
                    }
                  >
                    {Object.entries(REGION_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </StatusSelect>
                  <StatusSelect
                    value={region.visibility}
                    onChange={(e) =>
                      handleVisibilityChange(
                        region.id,
                        e.target.value as RegionVisibility
                      )
                    }
                  >
                    {Object.entries(REGION_VISIBILITY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </StatusSelect>
                  {!region.isDefault && (
                    <ActionButton
                      $variant="warning"
                      onClick={() => handleSetDefault(region.id)}
                    >
                      <Star size={12} />
                      Set Default
                    </ActionButton>
                  )}
                </ActionButtons>

                {region.historicalPeriods && region.historicalPeriods.length > 0 && (
                  <HistoricalPeriods>
                    <PeriodTitle>
                      <Calendar size={12} style={{ display: "inline", marginRight: "4px" }} />
                      Historical Periods ({region.historicalPeriods.length})
                    </PeriodTitle>
                    <PeriodList>
                      {region.historicalPeriods.map((period) => (
                        <PeriodBadge key={period.id} title={period.description}>
                          {period.name}
                          {period.startYear && ` (${period.startYear < 0 ? `${Math.abs(period.startYear)} BCE` : period.startYear})`}
                        </PeriodBadge>
                      ))}
                    </PeriodList>
                  </HistoricalPeriods>
                )}

                {region.tags && region.tags.length > 0 && (
                  <div style={{ marginTop: "1rem" }}>
                    <PeriodTitle>Tags</PeriodTitle>
                    <PeriodList>
                      {region.tags.map((tag) => (
                        <PeriodBadge key={tag}>#{tag}</PeriodBadge>
                      ))}
                    </PeriodList>
                  </div>
                )}
              </CardBody>
            </RegionCard>
          ))}
        </RegionGrid>
      )}
    </Container>
  );
};

export default RegionManagement;

